import type { BifrostOptions } from '@atlassian/pillar/bifrost-descriptor/types';

import type { UrlMatch } from './matchUrl.ts';
import { matchUrl } from './matchUrl.ts';
import { toUrl } from './toUrl.ts';

export type RouteMatch = UrlMatch & {
	name: string;
};

/**
 * Default for routes with no `bifrost` config, so Volt serves them instead of the monolith. No
 * `bundling`, so no SSR entry is generated. `exact` matters: `BASE` is `/`, so a non-exact entry
 * would claim every path below it. Shared and frozen; ssr-app's pillar config clones it.
 */
const CLIENT_ONLY_VOLT_BIFROST: BifrostOptions = Object.freeze({
	indexHtml: 'error-app.html',
	voltRouteGate: 'bifrost-gateway-client-volt-killswitch',
	exact: true,
});

export type RouteProps = {
	name: string;
	pattern: string;
	// An optional condition to check in match()
	condition?: (match: UrlMatch) => boolean;
	// Determine whether a route change is a transition
	// Default:
	// - true if no previous route
	// - true if pathname is not the same
	isTransition?: (previous: RouteMatch, current: RouteMatch) => boolean;
	ufoName?: string;
	/** Omit for {@link CLIENT_ONLY_VOLT_BIFROST}; `null` to stay out of the descriptor. */
	bifrost?: BifrostOptions | null;
};

export class Route {
	static matchFirst(routes: Route[], url: string): RouteMatch | null {
		for (const route of routes) {
			if (!(route instanceof Route)) {
				continue;
			}
			const matchResult = route.match(url);
			if (matchResult) {
				return matchResult;
			}
		}
		return null;
	}

	readonly name: string;
	readonly ufoName?: string;
	readonly bifrost?: BifrostOptions;
	readonly pattern: string;
	readonly condition: (match: UrlMatch) => boolean;
	readonly isTransition: (previous: RouteMatch | null, current: RouteMatch) => boolean;

	constructor(props: RouteProps) {
		this.name = props.name;
		this.ufoName = props.ufoName;
		// `null` opts out; checked before `??` so it is not treated as "not provided".
		this.bifrost = props.bifrost === null ? undefined : (props.bifrost ?? CLIENT_ONLY_VOLT_BIFROST);
		this.pattern = props.pattern;
		this.condition = props.condition ? props.condition : () => true;
		this.isTransition = (previous, current) => {
			if (!previous) return true;
			if (props.isTransition) {
				return previous.name === current.name ? props.isTransition(previous, current) : true;
			}
			return previous.pathname !== current.pathname;
		};
	}

	match(url: string): RouteMatch | null {
		const urlMatch = matchUrl(this.pattern, url);

		if (urlMatch) {
			if (this.condition && !this.condition(urlMatch)) {
				return null;
			}

			return {
				...urlMatch,
				name: this.name,
			};
		}

		return null;
	}

	toUrl(params?: Parameters<typeof toUrl>[1], options?: Parameters<typeof toUrl>[2]): string {
		return toUrl(this.pattern, params, options);
	}
}
